/**
 * AccountSection — Password change, account deletion, logout
 * Merges the former SecuritySection + AccountDangerSection.
 */

import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Input,
  InputField,
  Button,
  ButtonText,
  ButtonSpinner,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_SM, FS_MD, FS_LG } from '../../theme';
import { SectionCard, TextLinkButton } from '../ui';
import { useAuthStore } from '../../store/authStore';
import { useAccountDeletion } from '../../hooks/useAccountDeletion';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export const AccountSection = React.memo(function AccountSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const showBanner = useAppBannerStore((s) => s.showBanner);

  // --- Password ---
  const updatePassword = useAuthStore((s) => s.updatePassword);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const authError = useAuthStore((s) => s.error);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      showBanner({ message: t('profile.security.passwordMinLength'), type: 'warning' });
      return;
    }
    if (newPassword !== confirmPassword) {
      showBanner({ message: t('profile.security.passwordsMismatch'), type: 'warning' });
      return;
    }
    try {
      await updatePassword(newPassword);
      setIsChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
      showBanner({ message: t('profile.security.passwordUpdated'), type: 'success' });
    } catch (err: unknown) {
      showBanner({ message: (err as Error).message });
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  // --- Logout ---
  const logout = useAuthStore((s) => s.logout);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // --- Account Deletion ---
  const {
    deletionStatus,
    isFetchingStatus,
    fetchError: deletionFetchError,
    isLoading: isDeletionLoading,
    error: deletionError,
    requestDeletion,
    cancelDeletion,
    refreshStatus,
  } = useAccountDeletion();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const performDeletionRequest = async () => {
    setShowDeleteDialog(false);
    const success = await requestDeletion();
    if (success) {
      showBanner({ message: t('profile.account.deletionSuccess'), type: 'success' });
    } else if (deletionError) {
      showBanner({ message: deletionError });
    }
  };

  const handleCancelDeletion = async () => {
    const success = await cancelDeletion();
    if (success) {
      showBanner({ message: t('profile.account.cancellationSuccess'), type: 'success' });
    } else if (deletionError) {
      showBanner({ message: deletionError });
    }
  };

  return (
    <>
      <SectionCard title={t('profile.account.sectionTitle')}>
        {/* Password */}
        <VStack space="md">
          <HStack justifyContent="space-between" alignItems="center">
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}>
              {t('profile.security.title')}
            </Text>
            {!isChangingPassword && (
              <TextLinkButton testID="profile-button-password" accessibilityLabel={t('profile.security.changePasswordAccessibility')} label={t('profile.security.changePassword')} onPress={() => setIsChangingPassword(true)} />
            )}
          </HStack>

          {isChangingPassword && (
            <VStack space="md">
              <Box>
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }} mb="$1">
                  {t('profile.security.newPasswordLabel')}
                </Text>
                <Input variant="outline" size="md" testID="profile-input-new-password" accessibilityLabel={t('profile.security.newPasswordAccessibility')}>
                  <InputField
                    value={newPassword}
                    onChangeText={setNewPassword}
                    placeholder={t('profile.security.newPasswordPlaceholder')}
                    secureTextEntry
                    editable={!isAuthLoading}
                  />
                </Input>
              </Box>

              <Box>
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }} mb="$1">
                  {t('profile.security.confirmPasswordLabel')}
                </Text>
                <Input variant="outline" size="md" testID="profile-input-confirm-password" accessibilityLabel={t('profile.security.confirmPasswordAccessibility')}>
                  <InputField
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    placeholder={t('profile.security.confirmPasswordPlaceholder')}
                    secureTextEntry
                    editable={!isAuthLoading}
                  />
                </Input>
              </Box>

              <HStack space="md">
                <Button
                  testID="profile-button-cancel-password"
                  accessibilityLabel={t('common.cancel')}
                  accessibilityRole="button"
                  flex={1}
                  variant="outline"
                  onPress={handleCancelPasswordChange}
                  isDisabled={isAuthLoading}
                >
                  <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.text, ...ffs }}>{t('common.cancel')}</ButtonText>
                </Button>
                <Button
                  testID="profile-button-update-password"
                  accessibilityLabel={t('common.update')}
                  accessibilityRole="button"
                  flex={1}
                  style={{ backgroundColor: theme.primary }}
                  onPress={handleChangePassword}
                  isDisabled={isAuthLoading || !newPassword || !confirmPassword}
                >
                  {isAuthLoading ? (
                    <ButtonSpinner color="white" />
                  ) : (
                    <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>{t('common.update')}</ButtonText>
                  )}
                </Button>
              </HStack>
            </VStack>
          )}
        </VStack>

        {/* Account Deletion */}
        <VStack space="md">
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.error, ...ffs }}>
            {t('profile.account.title')}
          </Text>

          {isFetchingStatus ? (
            <HStack testID="profile-deletion-loading" space="sm" alignItems="center" p="$4">
              <ButtonSpinner color={theme.textSecondary} />
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('profile.account.checkingStatus')}
              </Text>
            </HStack>
          ) : deletionFetchError ? (
            <VStack testID="profile-deletion-error" space="sm">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('profile.account.couldntLoadStatus')}
              </Text>
              <Button
                testID="profile-button-retry-deletion"
                accessibilityLabel={t('common.retry')}
                accessibilityRole="button"
                size="md"
                variant="outline"
                style={{ borderColor: theme.border }}
                onPress={refreshStatus}
              >
                <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.text, ...ffs }}>{t('common.retry')}</ButtonText>
              </Button>
            </VStack>
          ) : deletionStatus?.hasPendingRequest ? (
            <Box testID="profile-deletion-warning" style={{ backgroundColor: theme.warning }} p="$4" rounded="$lg">
              <VStack space="sm">
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: 'white', ...ffs }}>
                  {t('profile.account.deletionScheduled')}
                </Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>
                  {t('profile.account.deletionMessage')}{' '}
                  <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: 'white', ...ffs }}>
                    {t('profile.account.day', { count: deletionStatus.daysRemaining })}
                  </Text>
                  .
                </Text>
                {deletionStatus.scheduledFor && (
                  <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
                    {t('profile.account.scheduledFor', { date: new Date(deletionStatus.scheduledFor).toLocaleDateString() })}
                  </Text>
                )}
                <Button
                  testID="profile-button-cancel-deletion"
                  accessibilityLabel={t('profile.account.cancelDeletion')}
                  accessibilityRole="button"
                  size="sm"
                  style={{ backgroundColor: 'white', marginTop: 8 }}
                  onPress={handleCancelDeletion}
                  isDisabled={isDeletionLoading}
                >
                  {isDeletionLoading ? (
                    <ButtonSpinner color={theme.warning} />
                  ) : (
                    <ButtonText allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.warning, ...ffs }}>{t('profile.account.cancelDeletion')}</ButtonText>
                  )}
                </Button>
              </VStack>
            </Box>
          ) : (
            <VStack space="sm">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('profile.account.deleteDescription')}
              </Text>
              <Button
                testID="profile-button-delete"
                accessibilityLabel={t('profile.account.deleteButton')}
                size="md"
                variant="outline"
                style={{ borderColor: theme.error }}
                onPress={() => setShowDeleteDialog(true)}
                isDisabled={isDeletionLoading || isAuthLoading}
              >
                {isDeletionLoading ? (
                  <ButtonSpinner color={theme.error} />
                ) : (
                  <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.error, ...ffs }}>{t('profile.account.deleteButton')}</ButtonText>
                )}
              </Button>
            </VStack>
          )}
        </VStack>

        {/* Logout */}
        <Box>
          {authError && (
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.error, textAlign: 'center', ...ffs }} mb="$4">
              {authError}
            </Text>
          )}
          <Button
            testID="profile-button-logout"
            accessibilityLabel={t('common.logout')}
            size="lg"
            variant="outline"
            style={{ borderColor: theme.warning }}
            onPress={() => setShowLogoutDialog(true)}
            isDisabled={isAuthLoading}
          >
            <ButtonText allowFontScaling={false} style={{ fontSize: FS_LG * fontScale, color: theme.warning, ...ffs }}>{t('common.logout')}</ButtonText>
          </Button>
        </Box>
      </SectionCard>

      {/* Logout confirmation */}
      <ConfirmDialog
        visible={showLogoutDialog}
        title={t('profile.logout.confirmTitle')}
        message={t('profile.logout.confirmMessage')}
        confirmLabel={t('common.logout')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={async () => {
          setShowLogoutDialog(false);
          await logout();
        }}
        onCancel={() => setShowLogoutDialog(false)}
      />

      {/* Delete account confirmation */}
      <ConfirmDialog
        visible={showDeleteDialog}
        title={t('profile.account.deleteConfirmTitle')}
        message={t('profile.account.deleteConfirmMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={performDeletionRequest}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
});
